import React from "react";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Calendar } from "lucide-react";

interface UnifiedTransaction {
  key: string;
  transactionType: string;
  instrumentName: string;
  current?: {
    baseAmount: number;
  };
  comparison?: {
    baseAmount: number;
  };
}

interface TransactionRowProps {
  unifiedTransaction: UnifiedTransaction;
  categoryKey: string;
  getCategoryBasedValueColor: (value: number, category?: string) => string;
}

export const TransactionRow: React.FC<TransactionRowProps> = ({
  unifiedTransaction,
  categoryKey,
  getCategoryBasedValueColor,
}) => {
  const currentValue = unifiedTransaction.current?.baseAmount || 0;
  const comparisonValue = unifiedTransaction.comparison?.baseAmount || 0;
  
  // Create a display name for the transaction
  const displayName = `${unifiedTransaction.transactionType} - ${unifiedTransaction.instrumentName}`;
  
  // Determine transaction status and styling
  let statusBadge = null;
  let rowIcon = <Calendar className="h-3 w-3 text-gray-500" />;
  
  if (!unifiedTransaction.current) {
    statusBadge = <Badge variant="outline" className="ml-2 text-red-600 border-red-300 bg-red-50">Removed</Badge>;
    rowIcon = <Calendar className="h-3 w-3 text-red-500" />;
  } else if (!unifiedTransaction.comparison) {
    statusBadge = <Badge variant="outline" className="ml-2 text-blue-600 border-blue-300 bg-blue-50">New</Badge>;
    rowIcon = <Calendar className="h-3 w-3 text-blue-500" />;
  }

  const formatValue = (value: number) => {
    if (value === 0) return '—';
    return (value >= 0 ? '' : '-') + '$' + Math.abs(value).toLocaleString();
  };

  return (
    <tr className="border-b hover:bg-gray-50 transition-colors">
      <td className="py-3 px-4">
        <div className="flex items-center gap-2" style={{ marginLeft: `${2 * 20}px` }}>
          {rowIcon}
          <span className="font-medium text-gray-800">{displayName}</span>
          {statusBadge}
        </div>
      </td>
      <td className={`py-3 px-4 text-right ${currentValue > 0 ? 'text-emerald-700 font-semibold' : currentValue < 0 ? 'text-red-700 font-semibold' : 'text-gray-600'}`}>
        <span className="font-mono">
          {formatValue(currentValue)}
        </span>
      </td>
      <td className={`py-3 px-4 text-right opacity-75 ${getCategoryBasedValueColor(comparisonValue, categoryKey)}`}>
        <div className="flex flex-col items-end gap-0.5">
          <span className="font-mono">
            {formatValue(comparisonValue)}
          </span>
          {(() => {
            const change = currentValue - comparisonValue;
            if (change === 0 || comparisonValue === 0) return null;
            
            const percentChange = ((change / Math.abs(comparisonValue)) * 100);
            const changeColor = change > 0 ? 'text-emerald-600' : 'text-red-600';
            
            return (
              <span className={`text-xs font-medium ${changeColor} opacity-75`}>
                {change > 0 ? '+' : ''}{percentChange.toFixed(1)}%
              </span>
            );
          })()}
        </div>
      </td>
      <td className="py-3 px-4 text-right">
        {(() => {
          const change = currentValue - comparisonValue;
          if (change === 0) return <span className="text-gray-500 font-mono text-sm">—</span>;
          
          const changeColor = change > 0 ? 'text-emerald-600' : 'text-red-600';
          const changeIcon = change > 0 ? 
            <TrendingUp className="h-3 w-3 text-emerald-500" /> : 
            <TrendingDown className="h-3 w-3 text-red-500" />;
          
          // Calculate percentage change
          const percentChange = comparisonValue !== 0 ? ((change / Math.abs(comparisonValue)) * 100) : null;
          const percentDisplay = percentChange !== null ? 
            `(${percentChange > 0 ? '+' : ''}${percentChange.toFixed(1)}%)` : 
            '(New)';
          
          return (
            <div className="flex flex-col items-end gap-0.5">
              <div className="flex items-center gap-1">
                {changeIcon}
                <span className={`font-mono text-sm font-medium ${changeColor}`}>
                  {change > 0 ? '+' : ''}{(change >= 0 ? '' : '-')}${Math.abs(change).toLocaleString()}
                </span>
              </div>
              <span className={`text-xs font-medium ${changeColor} opacity-75`}>
                {percentDisplay}
              </span>
            </div>
          );
        })()}
      </td>
    </tr>
  );
}; 